import { Component, OnDestroy, OnInit } from '@angular/core';
import { NgxSpinnerService } from 'ngx-spinner';
import { MenuItem } from 'primeng/api';
import { SubSink } from 'subsink';
import { AuthenticationService } from '../services/authentication.service';
import { PayrollService } from '../services/payroll.service';
import { PpsUtilityService } from '../services/pps-utility.service';
import { BusinessOperation } from '../shared/models/business-operation';
import { IDateRange } from '../shared/models/date-range';
import { PayrollTimesheet } from '../shared/models/payroll-timesheet';

@Component({
  selector: "pps-payroll-timesheet",
  templateUrl: "./payroll-timesheet.component.html",
  styles: [`
    :host ::ng-deep .custom-calendar-icon .p-button {
      color: black;
    }`]
})
export class PayrollTimesheetComponent implements OnInit, OnDestroy{
  private subs = new SubSink();
  smallScreen = false;

  items: MenuItem[] = [{label: "Payroll", icon: "pi-fw fas fa-hand-holding-usd"}];
  spinnerName: string = "payrollTimesheetSpinner";

  message: string = "";

  yearRange: string;

  selectedDateRange: IDateRange;
  payPeriods: IDateRange[] = [];
  payrollTimesheets: PayrollTimesheet[] = [];
  totalLines = 0;
  exporting: boolean = false;

  gridHeaders = [];

  private gridApi;
  gridOptions = {
    context: { componentParent: this },
    defaultColDef: {
      filter: true,
      sortable: true,
      resizable: true,
      floatingFilter: true
    }
  };  

  constructor(private _payrollService: PayrollService,
              private _authService: AuthenticationService,
              private spinner: NgxSpinnerService,
              private _util: PpsUtilityService){}

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  ngOnInit(){
    let year = new Date().getFullYear();
    this.yearRange = `${year-5}:${year+5}`;
        
    this.gridHeaders = this.getColumnDefs();
    this.payPeriods = this._payrollService.getPayPeriods(36);
    this.selectedDateRange = this.payPeriods[0];
    this.loadPayrollTimesheet();
  }

  onGridReady(params) {
    this.gridApi = params.api;
  }  

  onDateRangeChanged(event){
    if (this.selectedDateRange){
      this.loadPayrollTimesheet();
    }    
  }

  loadPayrollTimesheet(errorLineOnly: boolean = false){
    this.spinner.show(this.spinnerName);
    this.subs.sink = this._payrollService.getPayrollTimesheet(this.selectedDateRange)
      .subscribe(res => {
        if (res.succeed){
          if (res.result.length === 0){
            this.message = "No data found!";
          }else{
            this.totalLines = res.result.length;
            this.payrollTimesheets = errorLineOnly ? res.result.filter(pts => !pts.payCategoryId || pts.units === null) : res.result;
          }
        }
      }, 
      error => {},
      ()=> {
        this.spinner.hide(this.spinnerName);
      });
  }

  exportPayrollTimesheet(){
    this.exporting = true;
    this.subs.sink = this._payrollService.downloadPayrollTimesheets(
      {fromDate: this.selectedDateRange.range.from, toDate: this.selectedDateRange.range.to}, 
      () => this.exporting = false);
  }  

  canExportTimeSheet(){
    return this._authService.canUserDoOperartion(BusinessOperation.ExportPayrollTimesheet);
  }

  getNumberOfErrorLines(){
    return this.payrollTimesheets.filter(pts => !pts.payCategoryId || pts.units === null).length;
  }

  private getColumnDefs(){
    return [
      {
        headerName: 'Employee No',
        field:'employeeNo',
        width: 100
      },
      {
        headerName: 'Employee Name',
        field:'employee.position.title',
        width: 200,
        valueGetter: params => this._util.getEmployeeName({firstName: params.data.firstName, lastName: params.data.lastName}, true)
      },
      {
        headerName: 'Employee Position',
        field:'positionTitle',
        width: 200
      },
      {
        headerName: 'Job',
        field:'projectNo',
        width: 100
      },
      {
        headerName: 'Date',
        field:'day',
        width: 170,
        //filter: 'agDateColumnFilter',
        //filterParams: this.dateFilterParams,
        comparator: (date1, date2) => this._util.dateComparator(date1, date2),
        valueGetter: this.getDateValue
      },
      {
        headerName: 'Pay Category',
        field:'payCategory',
        width: 300, 
        cellStyle: params => this.getPayCategoryCellStyle(params)
      },
      {
        headerName: 'Units',
        field:'units',
        width: 100,
        filter: 'agNumberColumnFilter',
        filterParams: this.numberFilterParams,
        cellStyle: params => this.getUnitCellStyle(params),
        cellRenderer: params => this._util.formatNumber(params.data[params.colDef.field], 2)
      },
      {
        headerName: 'Day',
        field:'weekDayName',
        width: 80
      }
    ];
  }

  private getPayCategoryCellStyle(params){
    var ts = params.data;
    if (!ts.payCategoryId){
      return {background: "red", color: "yellow"};
    }

    return {};
  }

  private getUnitCellStyle(params){
    var ts = params.data;

    if (ts.units === null){
      return this._util.getNumericCellStyle({borderRightColor:'#eeeeee', background: "red", color: "yellow"});
    }

    return this._util.getNumericCellStyle({borderRightColor:'#eeeeee'});
  }


  private getDateValue(params){
    if (params.data[params.colDef.field]){
      let value = params.context.componentParent._util.getDateString(params.data[params.colDef.field]);
      return value;
    }

    return "";
  }

  private dateFilterParams = {
    comparator: this._util.filterDateComparator,
    filterOptions: ["equals", "lessThan", "greaterThan"],
    defaultOption: "greaterThan"
  };

  private numberFilterParams = {
    filterOptions: ["equals", "lessThan", "greaterThan"],
    defaultOption: "equals"
  };
}